import React, { useContext } from "react";
import { AuthContext } from "./providers/auth";
import { useLocation } from "react-router";

/**
 * App context providers
 */
import AppContexts from "./contexts";

/**
 * UI containers
 */
import Navigation from "../components/app/navigation/navigation";
import AuditorPageRoutes from "./routers/auditor-page-routes";
import ClientPageRoutes from "./routers/client-page-routes";
import GenericRoutes from "./routers/generic-routes";
import IsolatedRoutes from "./routers/isolated-routes";
import PartnerShell from "../components/app/partner-shell/partner-shell";
import PointsPartnerRoutes from "./routers/points-partner-routes";
import StaffPageRoutes from "./routers/staff-page-routes";

/**
 * Functional component to return the routing for the app. Checks are done at this level for calls to 
 * generic pages like signup/login or specifc calls wrapped in a user context for app-level pages 
 * like clients or content.
 */
function AppRoutes() {
    /**
     * Get the user object from the Auth context
     */
    const { user } = useContext(AuthContext);

    /**
     * Get the location of the current browser window
     */
    const location = useLocation();

    /**
     * Is the user loading through to a reservation page
     */
    if (location.pathname === "/reservation-request") {
        return (
            <IsolatedRoutes />
        );
    }

    /**
     * Public, client-facing online quotes. Rendered regardless of auth state so
     * the shareable /quote/:token link works for logged-out clients and for
     * staff who open the copy-link while signed in.
     */
    if (location.pathname.startsWith("/quote/")) {
        return (
            <IsolatedRoutes />
        );
    }

    /**
     * Is the auth context in a loading state
     */
    if (user.loading) {
        return "loading...";
    } else {
        /**
         * Check to see if the user is logged in
         */
        if (user.logged_in) {
            /**
             * If the user is logged in and the window is on the leaderboard page, we don't 
             * need to show a navigation
             */
            if (location.pathname === "/leaderboard") {
                return (
                    <IsolatedRoutes />
                );
            } else if (!user.is_staff && !user.is_auditor && user.is_points_partner) {
                /**
                 * Third-party Stableford redemption partners get their own shell.
                 * They have a single page to be on, so they are given the logo
                 * and a centred column rather than a navigation listing surfaces
                 * they cannot reach
                 */
                return (
                    <AppContexts>
                        <PartnerShell>
                            <PointsPartnerRoutes />
                        </PartnerShell>
                    </AppContexts>
                );
            } else {
                /**
                 * Otherwise show all other pages
                 */
                return (
                    <AppContexts>
                        <section id="app-screen-wrapper">
                            {/* Application navigation component */}
                            <Navigation />

                            {/* Main app DOM element */}
                            <div id="app-screen-height-contain">
                                <div id="app-screen-inner">
                                    {/* Page routes for staff accounts */}
                                    {user.is_staff && <StaffPageRoutes />}

                                    {/* Page routes for read-only third-party auditor accounts */}
                                    {!user.is_staff && user.is_auditor && <AuditorPageRoutes />}

                                    {/* Page routes for client accounts */}
                                    {!user.is_staff && !user.is_auditor && <ClientPageRoutes />}
                                </div>
                            </div>
                        </section>
                    </AppContexts>
                );
            }
        } else {
            /**
             * If they are not logged in, return the generic routes
             */
            return (
                <AppContexts>
                    {/* Main app DOM element */}
                    <div id="app-splash">
                        <GenericRoutes />
                    </div>
                </AppContexts>
            );
        }
    }
}

export default AppRoutes;